import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { createColumnHelper } from "@tanstack/react-table";
import TableStyle from "styles/application/TableStyle";
import PopupMenu from "components/Elements/PopupMenu";
import { ReactComponent as MenuIcon } from "assets/icons/3dot-icon.svg";
import { isoDateWithoutTimeZone } from "helpers/utils";
import { entityTypeFor } from "./entityRenderers";
import AuditRestoreDialog from "./AuditRestoreDialog";

const AT = TableStyle();

const helper = createColumnHelper();

const ReferenceCell = ({ row }) => {
	const { entityType, entityReference, entityId, linkEntityId, action, subsequentlyRestored } =
		row.original;
	const text = entityReference || `#${entityId}`;
	const { detailPath } = entityTypeFor(entityType);

	// Currently-deleted records have no destination; a delete that was later
	// restored links to the (live) record like any other row
	if (!detailPath || !linkEntityId || (action === "Deleted" && !subsequentlyRestored)) {
		return text;
	}

	return (
		<Link
			className="link-color"
			to={detailPath(linkEntityId)}
			target="_blank"
			rel="noopener noreferrer"
		>
			{text}
		</Link>
	);
};

const AUDIT_ACTION_KEYS = {
	Created: "option.auditActionCreated",
	Updated: "option.auditActionUpdated",
	Deleted: "option.auditActionDeleted",
	Restored: "option.auditActionRestored",
};

const ActionCell = ({ getValue, row }) => {
	const { t } = useTranslation();
	if (getValue() === "Deleted" && row.original.subsequentlyRestored)
		return t("label.deletedRestored");
	return t(AUDIT_ACTION_KEYS[getValue()] || getValue());
};

const PersonCell = ({ getValue }) => {
	const { t } = useTranslation();
	return getValue() || t("label.systemUser");
};

// Tracked properties read as column names ("PositionID") unless we name them.
// Terminology terms use their entity.* key so the tenant's wording wins; the
// rest use a plain label key. Anything unmapped falls back to the raw name.
const PROPERTY_LABEL_KEYS = {
	PositionID: "entity.position",
	Active: "label.active",
	Status: "label.status",
	DefectStatusID: "entity.defectStatus",
	FeedbackStatusID: "entity.feedbackStatus",
};

const propertyLabel = (propertyName, t) =>
	PROPERTY_LABEL_KEYS[propertyName] ? t(PROPERTY_LABEL_KEYS[propertyName]) : propertyName;

const DetailCell = ({ row }) => {
	const { t } = useTranslation();
	const { action, propertyName, oldValue, newValue, reason } = row.original;
	const name = propertyLabel(propertyName, t);
	const change =
		action === "Created" || propertyName === "IsDeleted" || propertyName === "ID"
			? ""
			: oldValue
			? `${name}: ${oldValue} → ${newValue ?? ""}`
			: `${name}: ${newValue ?? ""}`;
	if (!reason) return change;
	const reasonText = t("label.reasonValue", { reason });
	return change ? `${change} (${reasonText})` : reasonText;
};



// Self-contained row menu: only soft-deleted, still-present records offer Restore
const ActionsCell = ({ row }) => {
	const { t } = useTranslation();
	const [anchorEl, setAnchorEl] = useState(null);
	const [restoreOpen, setRestoreOpen] = useState(false);

	if (!row.original.canRestore) return null;

	// The dialog must render outside DotMenu: its portal clicks bubble through
	// the React tree, and landing on DotMenu's onClick would reopen the menu
	return (
		<>
			<AT.DotMenu
				onClick={(e) => setAnchorEl(anchorEl ? null : e.currentTarget)}
			>
				<AT.TableMenuButton>
					<MenuIcon />
				</AT.TableMenuButton>
				<PopupMenu
					index={0}
					selectedData={anchorEl ? 0 : null}
					anchorEl={anchorEl}
					id={row.original.id}
					clickAwayHandler={() => setAnchorEl(null)}
					menuData={[
						{
							name: t("action.restore"),
							handler: () => {
								setAnchorEl(null);
								setRestoreOpen(true);
							},
							isDelete: false,
						},
					]}
				/>
			</AT.DotMenu>
			{restoreOpen && (
				<AuditRestoreDialog
					row={row.original}
					onClose={() => setRestoreOpen(false)}
				/>
			)}
		</>
	);
};

export const buildAuditColumns = (t) =>
	helper.columns([
		helper.accessor("dateTime", {
			header: t("label.dateTime"),
			cell: ({ getValue }) => isoDateWithoutTimeZone(getValue()),
			meta: { sortable: true, minWidth: "160px" },
		}),
		helper.accessor("userName", {
			header: t("label.person"),
			cell: PersonCell,
			meta: { minWidth: "160px" },
		}),
		helper.accessor("action", {
			header: t("label.action"),
			cell: ActionCell,
			meta: { minWidth: "110px" },
		}),
		helper.accessor("entityType", {
			header: t("label.recordType"),
			cell: ({ getValue }) => entityTypeFor(getValue()).label(t, getValue()),
			meta: { minWidth: "110px" },
		}),
		helper.display({
			id: "reference",
			header: t("label.reference"),
			cell: ReferenceCell,
			meta: { minWidth: "120px" },
		}),
		helper.display({
			id: "detail",
			header: t("label.detail"),
			cell: DetailCell,
		}),
		helper.display({
			id: "actions",
			header: "",
			cell: ActionsCell,
			meta: { minWidth: "50px" },
		}),
	]);
